export type OcPlanTier = "growth" | "standard" | "enterprise" | "sandbox";
export type OcTenantStatus = "trial" | "active" | "suspended" | "canceled";
export type OcAdMechanism = "graph" | "ldap";

export interface OcPasswordPolicy {
  length: number;
  minUppercase: number;
  minLowercase: number;
  minDigits: number;
  minSymbols: number;
  allowedSymbols: string;
  excludeAmbiguous: boolean;
  mustChangeOnLogon?: boolean;
}

export interface OcTenant {
  id: string;
  name: string;
  slug: string;
  status: OcTenantStatus;
  planTier: OcPlanTier;
  isFounder: boolean;
  endsAt: string | null;
  cloudflareAccessAud: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OcTenantConfig {
  tenantId: string;
  adMechanism: OcAdMechanism;
  graphClientId: string | null;
  graphClientSecret: string | null;
  graphTenantId: string | null;
  ldapTunnelId: string | null;
  ldapBaseDn: string | null;
  ldapBindDn: string | null;
  ldapBindPassword: string | null;
  ouTemplate: string | null;
  usernameFormat: string | null;
  passwordPolicy: OcPasswordPolicy | null;
  emailDomain: string | null;
  passwordResetConnectionId: string | null;
  environmentType: "azure" | "on_prem" | "both" | null;
  updatedAt: string;
}

export interface OcTenantWebhookSecret {
  id: string;
  tenantId: string;
  secretHash: string;
  active: boolean;
  createdAt: string;
  rotatedAt: string | null;
}

export interface OcUserRole {
  id: string;
  tenantId: string;
  name: string;
  permissions: OcPermissionKey[];
  isSystemRole: boolean;
  createdAt: string;
}

export interface OcTenantUser {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  roleId: string;
  status: "active" | "invited" | "suspended";
  authMethod: "password" | "sso";
  ssoUsername: string | null;
  invitedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export type OcPermissionKey =
  | "students.view"
  | "students.provision"
  | "students.disable"
  | "students.activate"
  | "students.deactivate"
  | "students.delete"
  | "students.reset_password"
  | "students.bulk_action"
  | "reports.view"
  | "reports.export"
  | "reports.save"
  | "reports.schedule"
  | "dashboard.view"
  | "dashboard.configure"
  | "audit_log.view"
  | "ad_connection.view"
  | "ad_connection.configure"
  | "provisioning_rules.view"
  | "provisioning_rules.configure"
  | "email_templates.configure"
  | "agent.view"
  | "agent.manage"
  | "users.view"
  | "users.invite"
  | "users.manage"
  | "roles.manage"
  | "settings.view"
  | "settings.configure"
  | "billing.view"
  | "developer.view"
  | "support.view"
  | "search.view"
  | "search.configure"
  | "provisioning_settings.view"
  | "provisioning_settings.configure"
  | "alerts.view"
  | "alerts.configure"
  | "connections.view"
  | "connections.configure"
  | "actions.view"
  | "actions.configure"
  | "sso.view"
  | "sso.configure"
  // Self-service portal (end-user password reset / username lookup)
  | "selfservice.view"
  | "selfservice.configure"
  | "selfservice.log.view"
  | "selfservice.log.view_pii"
  // Wildcard — implicitly grants every permission, including ones added later.
  // Assigned to the system "Admin" role so new menu items never lock admins out.
  | "*";

// ── Permission catalog ─────────────────────────────────────────────────────────
// Single source of truth for the role editor. Groups mirror the sidebar menu
// 1:1 — every grantable permission corresponds to a menu action. Non-menu keys
// (ad_connection.*, agent.*, settings.*) still exist on OcPermissionKey for
// internal/back-end checks but are not surfaced here; the Admin wildcard
// covers them.

export const OC_WILDCARD_PERMISSION = "*" as const;

export const OC_PERMISSION_GROUPS: { label: string; keys: OcPermissionKey[] }[] = [
  // Overview
  { label: "Dashboard", keys: ["dashboard.view", "dashboard.configure"] },
  { label: "Directory Search", keys: ["students.view", "students.provision", "students.disable", "students.activate", "students.deactivate", "students.delete", "students.reset_password", "students.bulk_action"] },
  // Integrations
  { label: "Connections", keys: ["connections.view", "connections.configure"] },
  { label: "Workflows", keys: ["provisioning_rules.view", "provisioning_rules.configure"] },
  { label: "Record Management", keys: ["search.view", "search.configure"] },
  { label: "Actions Configuration", keys: ["actions.view", "actions.configure"] },
  // Provisioning
  { label: "Provision Rules", keys: ["provisioning_settings.view", "provisioning_settings.configure"] },
  // Reporting
  { label: "Audit Log", keys: ["audit_log.view"] },
  { label: "Self-Service Portal", keys: ["selfservice.view", "selfservice.configure", "selfservice.log.view", "selfservice.log.view_pii"] },
  { label: "Reports", keys: ["reports.view", "reports.export", "reports.save", "reports.schedule"] },
  { label: "Plan & Usage", keys: ["billing.view"] },
  { label: "Alerts", keys: ["alerts.view", "alerts.configure"] },
  // Settings
  { label: "Users & Roles", keys: ["users.view", "users.invite", "users.manage", "roles.manage"] },
  { label: "Email Templates", keys: ["email_templates.configure"] },
  { label: "SSO Settings", keys: ["sso.view", "sso.configure"] },
  { label: "Developer", keys: ["developer.view"] },
  // Help
  { label: "Support", keys: ["support.view"] },
];

// Every grantable (menu) permission — used to seed/refresh non-wildcard roles.
export const OC_ALL_MENU_PERMISSIONS: OcPermissionKey[] = OC_PERMISSION_GROUPS.flatMap((g) => g.keys);

// Wildcard-aware permission check. Use everywhere instead of a bare `.includes`.
export function ocHasPermission(
  permissions: readonly OcPermissionKey[] | readonly string[] | undefined | null,
  key: OcPermissionKey
): boolean {
  if (!permissions) return false;
  return permissions.includes(OC_WILDCARD_PERMISSION) || permissions.includes(key);
}

export interface OcSsoConfig {
  tenantId: string;
  apiAccessEnabled: boolean;
  ssoEnabled: boolean;
  ssoProtocol: "oidc" | null;
  oidcDiscoveryUrl: string | null;
  oidcClientId: string | null;
  oidcClientSecret: string | null;
  updatedAt: string;
}

// ─── Connection attribute (returned by /tenant/connections/attributes) ─────────

export interface OcConnectionAttribute {
  name: string;
  label: string;
  type: "string" | "boolean" | "datetime" | "collection" | "number";
  connectionId: string;
  connectionName: string;
  source: "azure_ad" | "on_prem_agent";
  example?: string;
}

// ─── Search Settings ──────────────────────────────────────────────────────────

export interface OcSearchDisplayAttribute {
  name: string;
  label: string;
  type: "text" | "badge" | "date" | "email" | "boolean" | "number";
  sortable?: boolean;
  width?: "sm" | "md" | "lg";
}

export interface OcSearchableAttribute {
  name: string;
  label: string;
  connectionId: string;
}

export interface OcSearchFullDetailSection {
  section: string;
  fields: OcSearchDisplayAttribute[];
}

export type OcRecordManagementAction =
  | "search"
  | "activate"
  | "deactivate"
  | "password_reset"
  | "security_groups"
  | "distribution_lists";

export interface OcSearchConnectionAction {
  connectionId: string;
  actions: OcRecordManagementAction[];
  /**
   * OU scoping for this connection's assignment. On-prem (on_prem_agent) only —
   * Azure/Entra has no OU concept and leaves these null/absent.
   *   - defaultOu: create target + directory search base. Required for an
   *     on-prem assignment.
   *   - disabledOu: where deactivated accounts are moved. Required only when the
   *     assignment includes "deactivate".
   */
  defaultOu?: string | null;
  disabledOu?: string | null;
}

export interface OcSearchSettings {
  tenantId: string;
  searchableAttributes: OcSearchableAttribute[];
  listColumns: OcSearchDisplayAttribute[];
  sideDetails: OcSearchDisplayAttribute[];
  fullDetails: OcSearchFullDetailSection[];
  connectionActions: OcSearchConnectionAction[];
  updatedAt: string;
}

// ─── Self-Service Portal ──────────────────────────────────────────────────────

export type OcSelfserviceAction = "password_reset" | "username_lookup";
export type OcSelfserviceProofingMethod = "email" | "sms";
export type OcSelfserviceResetStatus = "pending" | "verified" | "success" | "failed" | "expired" | "blocked";

export interface OcSelfserviceConnectionAction {
  connectionId: string;
  actions: OcSelfserviceAction[];
  priority: number;
  recoveryEmailAttribute?: string;
  recoveryPhoneAttribute?: string;
  /**
   * OU scoping for this self-service assignment. On-prem (on_prem_agent) only.
   *   - defaultOu: directory search base for resolving the user. Required for an
   *     on-prem assignment.
   *   - disabledOu: included for parity with Record Management; self-service does
   *     not deactivate, so it is generally unused here.
   */
  defaultOu?: string | null;
  disabledOu?: string | null;
}

export interface OcSelfserviceProofingChannels { allowEmail: boolean; allowSms: boolean; }

export interface OcSelfserviceSecurityPolicy {
  codeLength: number;
  codeTtlSeconds: number;
  maxAttempts: number;
  lockoutMinutes: number;
  lockoutMaxResetsPerDay: number;
}

export interface OcSelfserviceBranding {
  logoUrl?: string;
  primaryColor?: string;
  supportContactText?: string;
}

export interface OcSelfserviceSettings {
  tenantId: string;
  enabled: boolean;
  connectionActions: OcSelfserviceConnectionAction[];
  twilioConnectionId: string | null;
  smtpConnectionId: string | null;
  proofingChannels: OcSelfserviceProofingChannels;
  securityPolicy: OcSelfserviceSecurityPolicy;
  excludedGroups: string[];
  branding: OcSelfserviceBranding;
  logRetentionDays: number;
  updatedAt: string;
}

export interface OcSelfserviceResetLogEntry {
  id: string;
  tenantId: string;
  createdAt: string;
  completedAt: string | null;
  identifierSubmitted: string;
  resolvedUserDn: string | null;
  connectionId: string | null;
  proofingMethod: OcSelfserviceProofingMethod | null;
  proofingTargetMasked: string | null;
  verifyAttempts: number;
  ip: string | null;
  userAgent: string | null;
  country: string | null;
  status: OcSelfserviceResetStatus;
  failureReason: string | null;
  dispatchedCommandId: string | null;
  durationMs: number | null;
}

export interface OcTwilioConnectionConfig {
  accountSid: string;
  authToken: string;
  fromNumber: string;
}

export type OcSmtpConnectionConfig =
  | {
      provider: "mailgun";
      apiKey: string;
      domain: string;
      region?: "us" | "eu";
      fromAddress: string;
      fromName?: string;
    }
  | {
      provider: "sendgrid";
      apiKey: string;
      fromAddress: string;
      fromName?: string;
    }
  | {
      provider: "resend";
      apiKey: string;
      fromAddress: string;
      fromName?: string;
    };

// ─── Provisioning Settings ────────────────────────────────────────────────────

export interface OcUsernameSettings {
  format: string;
  transform: "lowercase" | "uppercase" | "none";
  domain?: string;
  collisionStrategy: "append_number" | "append_initials" | "append_middle_name" | "append_field_last_n" | "append_initial" | "append_birth_year" | "append_random" | "incremental_first_name";
  collisionFieldName?: string;
  collisionFieldDigits?: number;
}

export type OcConditionOperator =
  | "eq"
  | "neq"
  | "contains"
  | "starts_with"
  | "ends_with"
  | "in"
  | "not_in"
  | "is_empty"
  | "is_not_empty";

export interface OcFieldCondition {
  field: string;
  operator: OcConditionOperator;
  value: string | string[];
}

export interface OcOuRule {
  id: string;
  label: string;
  condition: OcFieldCondition | null;
  ouPath: string;
  priority: number;
}

export interface OcGroupRule {
  id: string;
  label: string;
  condition: OcFieldCondition | null;
  groupDns: string[];
  priority: number;
}

export interface OcProvisioningSettings {
  tenantId: string;
  username: OcUsernameSettings | null;
  passwordPolicy: OcPasswordPolicy | null;
  ouRules: OcOuRule[];
  defaultOu: string | null;
  securityGroupRules: OcGroupRule[];
  distributionListRules: OcGroupRule[];
  updatedAt: string;
}

// ─── Alert Preferences ────────────────────────────────────────────────────────

export interface OcAlertPreferences {
  tenantId: string;
  notifyJobFailure: boolean;
  notifyAgentStale: boolean;
  notifyWorkflowNoActivity: boolean;
  recipientUserIds: string[];
  updatedAt: string;
}
